import { ChangeDetectionStrategy, Component, computed, forwardRef, input, output, signal } from '@angular/core';
import { NgComponentOutlet } from '@angular/common';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { LucideFileText, LucideTrash, LucideUploadCloud } from '@lucide/angular';
import { FieldBase } from '../shared/field-base';
import { FieldErrorComponent } from '../shared/field-error.component';

/** Single-file upload with drag & drop and size validation. Value: File | null. */
@Component({
  selector: 'ds-file-upload',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgComponentOutlet, FieldErrorComponent],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => FileUploadComponent),
      multi: true,
    },
  ],
  template: `
    <div class="ds-field upload-field">
      <span class="ds-field__label" [id]="fieldId + '-label'" [class.ds-field__label--hidden]="hiddenLabel()">
        {{ label() }}
        @if (required()) {
          <span class="ds-field__required" aria-hidden="true">*</span>
        }
      </span>
      @if (helperText()) {
        <p class="ds-field__helper" [id]="fieldId + '-helper'">{{ helperText() }}</p>
      }

      <div
        class="zone"
        [class.zone--drag]="dragOver()"
        [class.zone--disabled]="disabled()"
        [class.zone--error]="shownError()"
        (dragover)="handleDragOver($event)"
        (dragleave)="dragOver.set(false)"
        (drop)="handleDrop($event)"
      >
        @if (value(); as file) {
          <span class="zone__file-icon" aria-hidden="true">
            <ng-container
              [ngComponentOutlet]="fileIcon"
              [ngComponentOutletInputs]="{ size: 32, strokeWidth: 1.75 }"
            />
          </span>
          <div class="zone__file-info">
            <span class="zone__file-name">{{ file.name }}</span>
            <span class="zone__file-size">{{ formatSize(file.size) }}</span>
          </div>
          <button
            type="button"
            class="zone__remove"
            [disabled]="disabled()"
            (click)="removeFile()"
          >
            <ng-container
              [ngComponentOutlet]="trashIcon"
              [ngComponentOutletInputs]="{ size: 20, strokeWidth: 2.25 }"
            />
            Hapus
          </button>
        } @else {
          <span class="zone__icon" aria-hidden="true">
            <ng-container
              [ngComponentOutlet]="uploadIcon"
              [ngComponentOutletInputs]="{ size: 40, strokeWidth: 1.75 }"
            />
          </span>
          <p class="zone__hint">Tarik berkas ke sini, atau</p>
          <button
            type="button"
            class="zone__browse"
            [disabled]="disabled()"
            [attr.aria-describedby]="fieldId + '-label ' + (describedBy() ?? '')"
            (click)="fileInput.click()"
            (blur)="onTouched()"
          >
            Pilih Berkas
          </button>
          <p class="zone__meta">{{ constraintText() }}</p>
        }
        <input
          #fileInput
          class="zone__native"
          type="file"
          tabindex="-1"
          aria-hidden="true"
          [accept]="accept() ?? ''"
          [disabled]="disabled()"
          (change)="handleFileChange($event)"
        />
      </div>

      @if (shownError(); as message) {
        <ds-field-error [errorId]="fieldId + '-error'">{{ message }}</ds-field-error>
      }
    </div>
  `,
  styles: `
    .upload-field {
      // max-width: 560px;
    }

    .ds-field__label--hidden {
      position: absolute;
      width: 1px;
      height: 1px;
      padding: 0;
      margin: -1px;
      overflow: hidden;
      clip: rect(0, 0, 0, 0);
      white-space: nowrap;
      border: 0;
    }

    .zone {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--space-2);
      padding: var(--space-6) var(--space-5);
      background: var(--color-surface);
      border: 2px dashed var(--color-border-input);
      border-radius: var(--radius-lg);
      text-align: center;
      transition: border-color var(--motion-fast), background-color var(--motion-fast);

      &--drag {
        border-color: var(--color-primary);
        background: var(--color-primary-tint);
      }

      &--error {
        border-color: var(--color-error);
      }

      &--disabled {
        background: var(--color-surface-sunken);
        border-color: var(--color-border);
      }
    }

    .zone__icon {
      color: #20335D;
    }

    .zone__hint {
      margin: 0;
      color: var(--color-text-secondary);
    }

    .zone__browse {
      min-height: var(--touch-target-comfort);
      padding: 0 var(--space-5);
      border: 2px solid var(--color-primary);
      border-radius: var(--radius-full);
      background: var(--color-surface);
      color: var(--color-primary);
      font-size: var(--text-md);
      font-weight: var(--font-weight-bold);
      cursor: pointer;
      transition: background-color var(--motion-fast);

      &:hover:not(:disabled) {
        background: var(--color-primary-tint);
      }

      &:disabled {
        border-color: var(--color-border);
        color: var(--color-text-disabled);
        cursor: not-allowed;
      }
    }

    .zone__meta {
      margin: 0;
      font-size: var(--text-sm);
      color: var(--color-text-secondary);
    }

    .zone__native {
      display: none;
    }

    /* selected-file row */
    .zone:has(.zone__file-info) {
      flex-direction: row;
      text-align: left;
      border-style: solid;
      border-color: var(--color-teal-300);
      background: var(--color-primary-tint);
    }

    .zone__file-icon {
      color: var(--color-primary);
    }

    .zone__file-info {
      flex: 1;
      display: flex;
      flex-direction: column;
      gap: 2px;
      min-width: 0;
    }

    .zone__file-name {
      font-weight: var(--font-weight-bold);
      overflow-wrap: anywhere;
    }

    .zone__file-size {
      font-size: var(--text-sm);
      color: var(--color-text-secondary);
    }

    .zone__remove {
      display: inline-flex;
      align-items: center;
      gap: var(--space-1);
      min-height: var(--touch-target);
      padding: 0 var(--space-3);
      border: 0;
      border-radius: var(--radius-full);
      background: transparent;
      color: var(--color-error-strong);
      font-size: var(--text-sm);
      font-weight: var(--font-weight-bold);
      cursor: pointer;
      transition: background-color var(--motion-fast);

      &:hover:not(:disabled) {
        background: var(--color-error-tint);
      }
    }
  `,
})
export class FileUploadComponent extends FieldBase<File> {
  /** e.g. '.pdf,.jpg,image/*' */
  accept = input<string | undefined>(undefined);
  maxSizeMb = input(5);
  acceptDescription = input<string | undefined>(undefined);
  hiddenLabel = input(false);

  readonly fileChange = output<File | null>();

  protected readonly uploadIcon = LucideUploadCloud;
  protected readonly fileIcon = LucideFileText;
  protected readonly trashIcon = LucideTrash;

  protected readonly dragOver = signal(false);
  protected readonly sizeError = signal<string | null>(null);

  protected readonly shownError = computed(() => this.errorText() ?? this.sizeError());

  protected readonly constraintText = computed(() => {
    const parts: string[] = [];
    if (this.acceptDescription()) parts.push(this.acceptDescription()!);
    parts.push(`Maksimal ${this.maxSizeMb()} MB`);
    return parts.join(' · ');
  });

  protected handleDragOver(event: DragEvent): void {
    if (this.disabled()) return;
    event.preventDefault();
    this.dragOver.set(true);
  }

  protected handleDrop(event: DragEvent): void {
    event.preventDefault();
    this.dragOver.set(false);
    if (this.disabled()) return;
    const file = event.dataTransfer?.files?.[0];
    if (file) this.setFile(file);
  }

  protected handleFileChange(event: Event): void {
    const inputEl = event.target as HTMLInputElement;
    const file = inputEl.files?.[0];
    if (file) this.setFile(file);
    inputEl.value = ''; // allow re-selecting the same file
  }

  protected removeFile(): void {
    this.sizeError.set(null);
    this.value.set(null);
    this.onChange(null);
    this.onTouched();
    this.fileChange.emit(null);
  }

  protected formatSize(bytes: number): string {
    if (bytes >= 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
    return `${Math.max(1, Math.round(bytes / 1024))} KB`;
  }

  private setFile(file: File): void {
    if (file.size > this.maxSizeMb() * 1024 * 1024) {
      this.sizeError.set(
        `Berkas terlalu besar (${this.formatSize(file.size)}). Maksimal ${this.maxSizeMb()} MB.`,
      );
      return;
    }
    this.sizeError.set(null);
    this.value.set(file);
    this.onChange(file);
    this.onTouched();
    this.fileChange.emit(file);
  }
}
